// app/javascript/controllers/cutoff_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["timer", "day", "deliveryDate"]
  static values  = {
    hour: Number,
    minute: Number,
    storageKey: { type: String, default: "cutoffHidden" },
    ttlMinutes: { type: Number, default: 30 } // hide for 30 minutes
  }

  connect() {
    if (this.isDismissed()) {
      this.element.remove()
      return
    }
    this.tick()
    this.interval = setInterval(() => this.tick(), 1000)
  }

  disconnect() {
    this.clearTimer()
  }

  // ===== Dismiss / persistence (30-min TTL) =====
  hide() {
    const expiresAt = Date.now() + this.ttlMinutesValue * 60 * 1000
    try {
      // store as JSON so we can evolve later if needed
      window.localStorage.setItem(this.storageKeyValue, JSON.stringify({ expiresAt }))
    } catch (_) { /* ignore storage issues */ }

    this.clearTimer()
    this.element.remove()
  }

  isDismissed() {
    try {
      const raw = window.localStorage.getItem(this.storageKeyValue)
      if (!raw) return false
      const { expiresAt } = JSON.parse(raw || "{}")
      // if expired, clean up and show again
      if (!expiresAt || Date.now() > Number(expiresAt)) {
        window.localStorage.removeItem(this.storageKeyValue)
        return false
      }
      return true
    } catch (_) {
      return false
    }
  }

  clearTimer() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
  }

  // ===== Business day helpers =====
  isWeekend(date) {
    const d = date.getDay()
    return d === 0 || d === 6 // Sun(0) / Sat(6)
  }

  addBusinessDays(date, n) {
    const d = new Date(date)
    while (n > 0) {
      d.setDate(d.getDate() + 1)
      if (!this.isWeekend(d)) n--
    }
    return d
  }

  nextBusinessDay(date) {
    const d = new Date(date)
    while (this.isWeekend(d)) d.setDate(d.getDate() + 1)
    return d
  }

  nextBusinessCutoff(now) {
    const base = this.nextBusinessDay(now)
    let cutoff = new Date(base)
    cutoff.setHours(this.hourValue, this.minuteValue, 0, 0)

    if (now > cutoff) {
      const nextBiz = this.addBusinessDays(cutoff, 1)
      cutoff = new Date(nextBiz)
      cutoff.setHours(this.hourValue, this.minuteValue, 0, 0)
    }
    return cutoff
  }

  humanDispatchDay(now, cutoff) {
    const sameYMD = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()

    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (sameYMD(now, cutoff)) return "today"
    if (sameYMD(tomorrow, cutoff)) return "tomorrow"
    return cutoff.toLocaleDateString(undefined, { weekday: "long" })
  }

  // ===== Tick =====
  tick() {
    const now = new Date()
    const cutoff = this.nextBusinessCutoff(now)

    const diffMs  = Math.max(0, cutoff - now)
    const hours   = Math.floor(diffMs / 1000 / 60 / 60)
    const minutes = Math.floor((diffMs / 1000 / 60) % 60)
    const seconds = Math.floor((diffMs / 1000) % 60)

    this.timerTarget.textContent =
      `${String(hours).padStart(2,"0")}:${String(minutes).padStart(2,"0")}:${String(seconds).padStart(2,"0")}`

    this.dayTarget.textContent = this.humanDispatchDay(now, cutoff)

    const delivery = this.addBusinessDays(cutoff, 1)
    this.deliveryDateTarget.textContent = delivery.toLocaleDateString(undefined, {
      weekday: "long", day: "numeric", month: "long"
    })
  }
}
